/**
 * CYCLE WORDS — the page runtime's part of `style-system/cycle-words.ts`
 * (job 9 in page-runtime.ts).
 *
 * An element carrying `data-pb-cycle` (its words, JSON) types each word in
 * turn: letters appear at `data-pb-cycle-speed` ms each, the finished word
 * stays `data-pb-cycle-hold` ms, then it is deleted twice as quickly and the
 * next word is typed. A blinking caret (a span this script adds, styled by
 * the page's CSS, no taller than the line) follows the letters unless
 * `data-pb-cycle-caret="off"`. The element keeps the height of its tallest
 * entry (reserveCycleHeight), so a word that wraps in a narrow column does
 * not move the page below it.
 *
 * The saved content is the starting point: when it is one of the words the
 * cycle carries on from there, otherwise it is deleted first. It pauses off
 * screen and while the tab is hidden, and never starts for anyone who
 * prefers reduced motion — the saved word simply stays. Assistive
 * technology gets the whole list as the element's name, so a screen reader
 * is not read a word a letter at a time.
 *
 * Rich text (bold, a link) cannot be typed a letter at a time; the element's
 * text is what cycles.
 *
 * The attribute helpers live here too, so the storefronts' mirrored copy
 * writes the same attributes Budflow's StyleWrapper does (as play-trigger.ts
 * does for play triggers). This file is bundled into the page runtime: keep
 * it free of imports.
 */

export const CYCLE_KEYS = [
  'style_animation_cycle',
  'style_animation_cycle_words',
  'style_animation_cycle_speed',
  'style_animation_cycle_hold',
  'style_animation_cycle_caret',
] as const;

/** Per letter typed. Deleting runs at half of this. */
export const CYCLE_SPEED_MS = 70;
/** A finished word stays this long before it is deleted. */
export const CYCLE_HOLD_MS = 2000;
const MIN_SPEED_MS = 10;
const MIN_HOLD_MS = 200;

type Props = Record<string, unknown>;

/** The designer's list: one entry per line, blank lines dropped. */
export function cycleWordsOf(props: Props): string[] {
  return String(props.style_animation_cycle_words ?? '')
    .split(/\r?\n/)
    .map((w) => w.trim())
    .filter((w) => w !== '');
}

/** On, with at least one word to type. */
export function cycleOn(props: Props): boolean {
  return props.style_animation_cycle === 'on' && cycleWordsOf(props).length > 0;
}

function msOf(value: unknown, fallback: number, min: number): number {
  const n = parseFloat(String(value ?? ''));
  return Number.isFinite(n) && String(value ?? '').trim() !== '' ? Math.max(min, Math.round(n)) : fallback;
}

export function cycleSpeedOf(props: Props): number {
  return msOf(props.style_animation_cycle_speed, CYCLE_SPEED_MS, MIN_SPEED_MS);
}

export function cycleHoldOf(props: Props): number {
  return msOf(props.style_animation_cycle_hold, CYCLE_HOLD_MS, MIN_HOLD_MS);
}

/**
 * The attributes the page runtime reads. Empty when the effect is off.
 * Budflow's StyleWrapper writes them; each storefront's `builderEffectAttrs`
 * calls this same function from its mirrored copy.
 */
export function cycleAttrs(props: Props): Record<string, string> {
  if (!cycleOn(props)) return {};
  const attrs: Record<string, string> = {
    'data-pb-cycle': JSON.stringify(cycleWordsOf(props)),
    'data-pb-cycle-speed': String(cycleSpeedOf(props)),
    'data-pb-cycle-hold': String(cycleHoldOf(props)),
  };
  if (props.style_animation_cycle_caret === 'off') attrs['data-pb-cycle-caret'] = 'off';
  return attrs;
}

export interface CycleOptions {
  words: string[];
  speed: number;
  hold: number;
  caret: boolean;
  /**
   * The block that keeps room for the longest word (reserveCycleHeight).
   * The element itself unless the words are typed into a span inside it, as
   * on the canvas.
   */
  host?: HTMLElement;
}

interface Cycling extends HTMLElement {
  __pbCycle?: {
    timer: number;
    inView: boolean;
    stop: () => void;
  };
}

/**
 * ROOM FOR THE LONGEST WORD. In a column too narrow for the longest entry
 * the line wraps while that word is up and springs back for the next: the
 * whole page below it jumps with every word. So the block is kept at the
 * height of its tallest entry — each word is set in place and measured
 * before the cycle starts, and again whenever the block's width changes.
 * The words are written into `text` (the element's text node, or the
 * element itself when there is none to keep); the block measured and held
 * is `host`. An inline host has no height to hold and is left alone.
 * Returns a release that stops following the width and lets the height go.
 */
export function reserveCycleHeight(host: HTMLElement, text: Text | HTMLElement, win: Window, words: string[]): () => void {
  const measure = () => {
    if (words.length === 0 || !host.isConnected) return;
    const cs = win.getComputedStyle(host);
    if (cs.display === 'inline' || cs.display === 'contents') return;
    const was = text.textContent ?? '';
    host.style.minHeight = '';
    let tallest = 0;
    for (const word of words) {
      text.textContent = word;
      tallest = Math.max(tallest, host.getBoundingClientRect().height);
    }
    text.textContent = was;
    if (tallest <= 0) return;
    // min-height is the content box unless the box is sized by its border.
    const edge = cs.boxSizing === 'border-box'
      ? 0
      : ['paddingTop', 'paddingBottom', 'borderTopWidth', 'borderBottomWidth']
        .reduce((sum, key) => sum + (parseFloat(cs[key as 'paddingTop']) || 0), 0);
    host.style.minHeight = `${Math.ceil(tallest - edge)}px`;
  };
  measure();

  const RO = (win as Window & typeof globalThis).ResizeObserver;
  let width = host.getBoundingClientRect().width;
  let frame = 0;
  const ro = typeof RO === 'function'
    ? new RO(() => {
      const now = host.getBoundingClientRect().width;
      if (now === width) return;
      width = now;
      // Next frame, not here: a measure changes the block's own height, and
      // a size change inside its observer's delivery is the browser's
      // "ResizeObserver loop" error (seen on the canvas).
      if (!frame) frame = win.requestAnimationFrame(() => { frame = 0; measure(); });
    })
    : null;
  ro?.observe(host);
  return () => {
    ro?.disconnect();
    if (frame) win.cancelAnimationFrame(frame);
    host.style.minHeight = '';
  };
}

const CYCLE = '[data-pb-cycle]';

export function readCycle(el: Element): CycleOptions | null {
  let words: unknown;
  try {
    words = JSON.parse(el.getAttribute('data-pb-cycle') || '[]');
  } catch {
    return null;
  }
  if (!Array.isArray(words)) return null;
  const list = words.map((w) => String(w)).filter((w) => w.trim() !== '');
  if (list.length === 0) return null;
  const speed = Math.max(MIN_SPEED_MS, parseInt(el.getAttribute('data-pb-cycle-speed') || '', 10) || CYCLE_SPEED_MS);
  const hold = Math.max(MIN_HOLD_MS, parseInt(el.getAttribute('data-pb-cycle-hold') || '', 10) || CYCLE_HOLD_MS);
  return { words: list, speed, hold, caret: el.getAttribute('data-pb-cycle-caret') !== 'off' };
}

/**
 * Start typing on one element. Returns a stop function. The element's text
 * becomes a single text node (plus the caret) so each letter is one write.
 */
export function startCycle(el: HTMLElement, win: Window, opts: CycleOptions): () => void {
  const node = el as Cycling;
  if (node.__pbCycle) node.__pbCycle.stop();
  const doc = el.ownerDocument;
  const { words, speed, hold, caret } = opts;
  if (!el.getAttribute('aria-label')) el.setAttribute('aria-label', words.join(', '));

  const saved = (el.textContent || '').trim();
  const text = doc.createTextNode(saved);
  el.textContent = '';
  el.appendChild(text);
  if (caret) {
    const bar = doc.createElement('span');
    bar.className = 'pb-caret';
    bar.setAttribute('aria-hidden', 'true');
    // The caret is drawn in the text's colour. Gradient text has no colour
    // (transparent, the gradient clipped to the glyphs), so the caret takes
    // the gradient itself — found on the element or the text it sits in.
    const paint = gradientBehind(el, win);
    if (paint) bar.style.backgroundImage = paint;
    el.appendChild(bar);
  }
  const release = reserveCycleHeight(opts.host ?? el, text, win, words);

  // Where to begin: carry on from the saved word when it is in the list.
  const at = words.indexOf(saved);
  let index = at >= 0 ? at : words.length - 1;
  let shown = saved;
  let phase: 'hold' | 'delete' | 'type' = at >= 0 ? 'hold' : 'delete';
  const state = { timer: 0, inView: true, stop: () => undefined as void };
  node.__pbCycle = state;

  const schedule = (ms: number) => {
    state.timer = win.setTimeout(tick, ms);
  };
  const tick = () => {
    if (!el.isConnected) {
      stop();
      return;
    }
    // Off screen or in a hidden tab: wait without moving on.
    if (!state.inView || doc.hidden) {
      schedule(250);
      return;
    }
    if (phase === 'hold') {
      phase = 'delete';
      schedule(hold);
      return;
    }
    if (phase === 'delete') {
      if (shown.length > 0) {
        shown = shown.slice(0, -1);
        text.data = shown;
        schedule(Math.max(10, Math.round(speed / 2)));
        return;
      }
      index = (index + 1) % words.length;
      phase = 'type';
      schedule(speed * 3);
      return;
    }
    const target = words[index];
    if (shown.length < target.length) {
      shown = target.slice(0, shown.length + 1);
      text.data = shown;
      schedule(speed);
      return;
    }
    phase = 'hold';
    schedule(0);
  };

  const IO = (win as Window & typeof globalThis).IntersectionObserver;
  const io = typeof IO === 'function'
    ? new IO((entries) => {
      for (const en of entries) state.inView = en.isIntersecting;
    })
    : null;
  io?.observe(el);

  const stop = () => {
    win.clearTimeout(state.timer);
    io?.disconnect();
    release();
    if (node.__pbCycle === state) delete node.__pbCycle;
  };
  state.stop = stop;
  // The first move: a saved word holds, anything else is cleared away.
  schedule(phase === 'hold' ? 0 : speed);
  return stop;
}

/** The text gradient a transparent-coloured text is painted with, if any. */
function gradientBehind(el: HTMLElement, win: Window): string | null {
  const style = (node: Element) => win.getComputedStyle(node);
  const colour = style(el).color;
  const transparent = colour === 'transparent' || /^rgba\(.*,\s*0\)$/.test(colour);
  if (!transparent) return null;
  let node: Element | null = el;
  for (let depth = 0; node && depth < 3; depth++) {
    const image = style(node).backgroundImage;
    if (image && image !== 'none' && image.includes('gradient')) return image;
    node = node.parentElement;
  }
  return null;
}

export function stopCycle(el: HTMLElement): void {
  (el as Cycling).__pbCycle?.stop();
}

const bound = new WeakSet<Element>();

/** Bind every `[data-pb-cycle]` on the page once. Nothing under reduced motion. */
export function bindCycleWords(doc: Document, win: Window): void {
  if (win.matchMedia && win.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  doc.querySelectorAll(CYCLE).forEach((el) => {
    if (bound.has(el)) return;
    const opts = readCycle(el);
    if (!opts) return;
    bound.add(el);
    startCycle(el as HTMLElement, win, opts);
  });
}

export const CYCLE_SELECTOR = CYCLE;
